import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Alert, Button, Col, Row, Space, Tabs } from "antd";
import { ComponentTitle, PageTitleLastCol } from "../custom-components";
import { StledSpin } from "../custom-components";
import SlotTableCore from "../batch_jobs/components/SlotTableCore";
import { InventorySummaryComponent } from "../batch_jobs/InventorySummaryComponent";
import { getAdSelectionCounts } from "../batch_jobs/utils/slotTableUtils";
import usePreSeedingHook from "./hooks/usePreSeedingHook";

const PreSeedingComponent: React.FC = () => {
    const location = useLocation();
    const [batchJobId, setBatchJobId] = useState<string | null>(null);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        setBatchJobId(params.get("batchJobId"));
    }, [location.search]);

    const {
        loading,
        submitting,
        slots,
        scheduleData,
        slotSelectionDict,
        setSlotSelectionDict,
        slotPositionDict,
        setSlotPositionDict,
        redirectToBatchJobs,
        error,
        getOriginalAdSelectionCounts,
        handleSubmitPreSeed,
        handleSkipPreSeed,
    } = usePreSeedingHook(batchJobId);

    if (redirectToBatchJobs) {
        return <Navigate to="/batch-jobs" />;
    }

    if (!batchJobId) {
        return (
            <Alert
                type="error"
                message="Missing batchJobId"
                description="No batch job ID was provided in the URL. Please go back and try again."
                showIcon
            />
        );
    }

    return (
        <StledSpin spinning={loading}>
            <Space direction="vertical" style={{ width: "100%" }} size="large">
                <Row justify="space-between" align="middle">
                    <Col>
                        <ComponentTitle>
                            Pre-Seed Ads for AutoPodder
                        </ComponentTitle>
                    </Col>
                    <PageTitleLastCol>
                        <Button
                            type="primary"
                            loading={submitting}
                            disabled={submitting || loading}
                            onClick={handleSubmitPreSeed}
                        >
                            {submitting ? 'Submitting...' : 'Run AutoPodder'}
                        </Button>
                        &nbsp;
                        <Button
                            type="default"
                            loading={submitting}
                            disabled={submitting || loading}
                            onClick={handleSkipPreSeed}
                        >
                            Skip Pre-Seeding
                        </Button>
                        &nbsp;
                        <Button
                            type="default"
                            onClick={() => {
                                window.location.href = '/batch-jobs';
                            }}
                        >
                            Cancel
                        </Button>
                    </PageTitleLastCol>
                </Row>

                {error && (
                    <Alert
                        type="error"
                        message="Error loading pre-seed data"
                        description={error}
                        showIcon
                    />
                )}

                {!loading && !error && (
                    <Alert
                        type="info"
                        message="Pre-Seeding (Optional)"
                        description="Assign specific ads to specific slots before running AutoPodder. AutoPodder will fill remaining empty slots automatically. You can skip this step to let AutoPodder handle everything."
                        showIcon
                        style={{ marginBottom: 8 }}
                    />
                )}

                <Tabs
                    defaultActiveKey="preseed-slots"
                    items={[
                        {
                            key: 'preseed-slots',
                            label: 'Pre-Seed Slots',
                            children: (
                                <SlotTableCore
                                    poddedData={slots}
                                    scheduleData={scheduleData}
                                    slotSelectionDict={slotSelectionDict}
                                    setSlotSelectionDict={setSlotSelectionDict}
                                    loading={loading}
                                    getOriginalAdSelectionCounts={getOriginalAdSelectionCounts}
                                    mode="preseed"
                                    slotPositionDict={slotPositionDict}
                                    setSlotPositionDict={setSlotPositionDict}
                                />
                            ),
                        },
                        {
                            key: 'inventory-summary',
                            label: 'Inventory Summary',
                            children: Object.keys(scheduleData).length > 0 ? (
                                <InventorySummaryComponent
                                    scheduleData={scheduleData}
                                    loading={loading}
                                    getAdSelectionCounts={() => getAdSelectionCounts(slots, slotSelectionDict)}
                                    getOriginalAdSelectionCounts={getOriginalAdSelectionCounts}
                                />
                            ) : <div>No inventory data available</div>,
                        },
                    ]}
                />
            </Space>
        </StledSpin>
    );
};

export default PreSeedingComponent;
